<template>
  <div>
    <div class="grey600 px-4 py-3">
      <v-row>
        <v-col cols="12" md="6" class="d-flex align-center">
          <v-img
            :width="56"
            class="flex-grow-0 header-level green800 mr-3"
            :aspect-ratio="1" contain
            :src="require(`@/assets/img/levels/tiers/${tier}-large.png`)"
          />
          <div>
            <h4
              class="green100--text lh-1-4 text-body-1 font-weight-semibold"
            >
              <span class="mr-1" v-text="$tc('common.level', level)" />
              <span class="green200--text text-caption">({{ xp | toLocaleInt }} XP)</span>
            </h4>

            <UserXPBar class="xp-bar" :xp="xp" />
          </div>
        </v-col>
        <v-col cols="12" md="6" class="d-flex justify-end align-center">
          <v-btn
            v-if="claimable.length"
            small tile depressed :ripple="false"
            color="green700 green200--text"
            :loading="loading.claim"
            @click="onClaimLevels()"
          >
            Claim all rewards ({{ claimable.length }})
          </v-btn>

          <v-menu
            v-else-if="unopenedCases.length"
            offset-y bottom left nudge-bottom="8"
            open-on-hover
            max-width="320"
            :disabled="!levelCaseCooldown"
            content-class="rounded-0"
          >
            <template #activator="{ on }">
              <div v-on="on">
                <v-btn

                  small tile depressed :ripple="false"
                  color="green700 green200--text"
                  :loading="loading.cooldown"
                  :disabled="levelCaseCooldown > 0"
                  min-width="100"
                  @click="onOpenCase(unopenedCases[0])"
                >
                  <Countdown
                    v-if="levelCaseCooldown > 0"
                    :duration="levelCaseCooldown / 1000"
                    format="HH:mm:ss"
                    class="text-body-2"
                    @finish="setLevelCaseCooldown(0)"
                  />
                  <span v-else>Open tier {{ unopenedCases[0].data.tier }} case</span>
                </v-btn>
              </div>
            </template>
            <v-card color="grey500" class="pa-4 rounded-0">
              <p v-if="levelCaseCooldown > 0" class="text-body-2 font-weight-semibold primary400--text mb-0 lh-1-3">
                Tiers have different cooldowns.<br>
                <b>Level up to clear your cooldown!</b>
              </p>
            </v-card>
          </v-menu>
        </v-col>
      </v-row>
    </div>

    <!-- slider -->
    <AccountLevelsSlider
      :config="config"
      :xp="xp"
      :claimed-levels="claimedLevels"
      :loading="loading.claim"
    />

    <!-- tier cases -->
    <div class="grey600 px-4 py-3">
      <v-row>
        <v-col cols="12">
          <h4 class="text-body-2 font-weight-bold green400--text text-uppercase mb-1">
            Level cases
          </h4>
          <p class="text-body-2 mb-0">
            Click on a tier to see the contents of that case. Level cases are opened from highest to lowest tier.
          </p>
        </v-col>

        <v-col cols="12" class="mt-n2 d-flex align-center flex-wrap">
          <div v-for="(_, cTier) in (new Array(config.maxTier + 1))" :key="cTier">
            <div class="tier-case link" @click="onPreviewCase(cTier)">
              <v-badge
                :value="!!unopenedCasesByTier[cTier]"
                color="primary500"
                top overlap bordered
                offset-x="32"
                right
              >
                <template v-if="unopenedCasesByTier[cTier]" #badge>
                  {{ unopenedCasesByTier[cTier].length }}x
                </template>
                <v-img
                  :width="36"
                  class="flex-grow-0 header-level grey400 mr-3"
                  :aspect-ratio="1" contain
                  :src="require(`@/assets/img/levels/tiers/${cTier}-large.png`)"
                />
              </v-badge>
            </div>
          </div>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import sortBy from 'lodash.sortby'
import groupBy from 'lodash.groupby'
import { LEVELS } from '@/utils/constants'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import AccountLevelsSlider from './AccountLevelsSlider'
import UserXPBar from './UserXPBar'

export default {
  components: { UserXPBar, AccountLevelsSlider },
  props: {
    xp: {
      type: Number,
      default: 0,
    },
    config: {
      type: Object,
      required: true,
    },
    claimedLevels: {
      type: Array,
      default: () => [],
    },
    rewards: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: {
        claim: false,
        cooldown: false,
      },
    }
  },
  computed: {
    ...mapGetters({
      levelCaseCooldown: 'user/levelCaseCooldown',
    }),
    LEVELS() {
      return LEVELS
    },
    maxTier() {
      return this.config.maxTier
    },
    level() {
      const nextLevel = LEVELS.findIndex(lvl => lvl > this.xp)

      // we return nextLevel because index starts at 0 and first level is 1
      return (nextLevel !== -1 ? nextLevel : LEVELS.length) - 1
    },
    claimable() {
      const { claimedLevels, level } = this

      return Object.keys(LEVELS).map(i => +i).filter(i => {
        if (i === 0 || i > level) return false
        return !claimedLevels.includes(i)
      })
    },
    tier() {
      return this.toTier(this.level)
    },
    unopenedCases() {
      const levelCases = this.rewards.filter(i => i.type === 'level_case')
      return sortBy(levelCases, 'data.tier').reverse()
    },
    unopenedCasesByTier() {
      return groupBy(this.unopenedCases, 'data.tier')
    },
  },
  mounted() {
    this.onGetCooldown()
  },
  beforeDestroy() {
    if (this.flickity) {
      this.flickity.destroy()
    }
  },
  methods: {
    ...mapActions({
      claimLevelRewards: 'user/claimLevelRewards',
      getLevelCaseCooldown: 'user/getLevelCaseCooldown',
    }),
    ...mapMutations({
      setLevelCaseCooldown: 'user/setLevelCaseCooldown',
    }),
    toTier(level) {
      return Math.min(Math.floor(level / this.config.tierUpgradeEvery), this.maxTier)
    },
    async onGetCooldown() {
      this.loading.cooldown = true
      try {
        await this.getLevelCaseCooldown()
      } catch (error) {
        this.$toast.error(error.message)
      } finally {
        this.loading.cooldown = false
      }
    },
    async onClaimLevels() {
      this.loading.claim = true
      try {
        await this.claimLevelRewards()

        this.$toast.success('Successfully claimed level rewards.')
        this.setLevelCaseCooldown(0)

        // NOTE gleam
        const Gleam = window.Gleam || []
        Gleam.push(['level-rewards-claim-RtBvRLLRe2MQ4', ''])
      } catch (error) {
        this.$toast.error(error.message)
      } finally {
        this.loading.claim = false
      }
    },
    async onOpenCase(reward) {
      const { data: { tier }, _id: rewardId } = reward
      this.$modal.open('level-case', { maxWidth: 800 }, { tier, rewardId })
    },
    async onPreviewCase(tier) {
      this.$modal.open('level-case', { maxWidth: 800 }, { tier, preview: true })
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .user-xp {
  min-width: 240px;
}
</style>
